import axios from 'axios';
import { Feedback, FeedbackStats, FeedbackFilters } from '../types/feedback';
import { User } from '../types/users';
import { EmailTemplate } from '../types/emailTemplates';
import { Cost } from '../types/infra_cost';
import GlobalVariable from '../config';
const API_BASE_URL = process.env.REACT_APP_API_URL || GlobalVariable.BASE_API_URL;

// https://adminstandoutapi.infuture.ai/api
// http://localhost:3001/api


const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 40000,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface CustomError extends Error {
  status?: number;
}
// Axios interceptors remain unchanged as they are fine
api.interceptors.request.use(
  (config) => {
    // console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status || 500;
    let message = 'Something went wrong';

    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      message = 'Network error: Unable to reach the server. Please check your internet connection or the backend server.';
    } else if (error.response?.status) {
      message = error.response.data?.message || error.message || message;
      if (status === 400) message = "Bad Request, Please try again"
      if (status === 401) message = 'Unauthorized. Please log in again.';
      if (status === 403) message = 'Forbidden.';
      if (status === 408) message = "Request Timeout"
      if (status === 404) message = 'API endpoint not found.';
      if (status >= 500) message = 'Server error occurred. Please try later.';
    } else if (error.message) {
      message = error.message;
    }

    const err = new Error(message) as CustomError;
    err.status = status;
    return Promise.reject(err);
  }
);

export const feedbackAPI = {
  // Get all feedback with optional filters
  async getFeedback(filters?: FeedbackFilters): Promise<Feedback[]> {
    try {
      const params = new URLSearchParams(window.location.search);

      if (filters?.search) params.append('search', filters.search);
      if (filters?.rating) params.append('rating', filters.rating.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await api.get(`/feedback?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  // Get feedback statistics
  async getStats(): Promise<FeedbackStats> {
    try {
      const response = await api.get('/feedback/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  },

  // Get feedback by ID
  async getFeedbackById(id: string): Promise<Feedback> {
    try {
      const response = await api.get(`/feedback/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback by ID:', error);
      throw error;
    }
  },

  // Health check
  async healthCheck(): Promise<{ status: string; message: string }> {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }
};

export const userAPI = {
  async getUsers() {
    const response = await api.get('/users');
    return response.data;
  },

  async updateUser(user_id: string, data: Partial<User>) {
    const response = await api.put(`/users/${user_id}`, data);
    return response.data;
  },

  async updateUserFreeTrial(user_id: string, data: Partial<User>) {
    const response = await api.put(`/users/free-trial/${user_id}`, data);
    return response.data;
  },

  async updateBulkTrials(data: { userIds: string[]; is_trial: boolean; trial_end: string; has_used_trial: boolean }) {
    const response = await api.patch('/users/update-trial', data);
    return response.data;
  },

  async getFilteredUsers(params: any) {
    const response = await api.get('/users/filter', { params });
    return response.data;
  }
}

export const rejectedJobsAPI = {
  async getRejectedJobs() {
    const response = await api.get('/rejected-jobs');
    return response.data;
  }
}
export const featuresAPI = {
  async getFeatures(selectedFeature: string) {
    const response = await api.get(`/indian-features/${selectedFeature}`)
    return response.data;
  }
}
export const companiesAPI = {
  async getCompanies(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    const response = await api.get('/companies', { params });
    return response.data;
  },

  async getCompanyById(companyId: string) {
    const response = await api.get(`/companies/${companyId}`);
    return response.data;
  }
};

export const emailTemplatesAPI = {
  async getTemplates(region?: string): Promise<EmailTemplate[]> {
    const res = await api.get('/email-templates', { params: { region } });
    return res.data;
  },

  async createTemplate(template: EmailTemplate, region?: string) {
    const res = await api.post('/email-templates', template, { params: { region } });
    return res.data;
  },

  async updateTemplate(id: string, template: EmailTemplate, region?: string) {
    const res = await api.put(`/email-templates/${id}`, template, { params: { region } });
    return res.data;
  },

  async deleteTemplate(id: string) {
    const res = await api.delete(`/email-templates/${id}`);
    return res.data;
  }
};
export const infraCostAPI = {
  getAllServices: async () => {
    const res = await api.get('/cost/services');
    return res.data;
  },
  createServices: async (limit: any) => {
    const res = await api.post('/cost/services', limit);
    return res.data;
  },
  async getCosts(service = 'All') {
    const query = service && service !== 'All' ? `?service=${encodeURIComponent(service)}` : '';
    const response = await api.get(`/cost${query}`);
    return response.data;
  },
  async createCost(cost: Cost) {
    const response = await api.post('/cost', cost);
    return response.data;
  },
  async updateCost(id: string, cost: Cost) {
    const response = await api.put(`/cost/${id}`, cost);
    return response.data;
  },
  async deleteCost(id: string) {
    const response = await api.delete(`/cost/${id}`);
    return response.data;
  },
  async getfilterCost() {
    const response = await api.get('/cost/filters');
    return response.data;
  }
};

export const featureModulesAPI = {
  async getModules() {
    const response = await api.get('/modules');
    return response.data;
  },

  async createModule(data: { name: string; description: string }) {
    const response = await api.post('/modules', data);
    return response.data;
  },

  async updateModule(id: string, data: { name: string; description: string }) {
    const response = await api.put(`/modules/${id}`, data);
    return response.data;
  },

  async deleteModule(id: string) {
    const response = await api.delete(`/modules/${id}`);
    return response.data;
  },

  async addFeature(moduleId: string, featureData: {
    title: string;
    description: string;
    status: 'active' | 'expired';
    expires_at: string;
  }) {
    const response = await api.post(`/modules/${moduleId}/features`, featureData);
    return response.data;
  },

  async updateFeature(moduleId: string, featureId: string, featureData: {
    title: string;
    description: string;
    status: 'active' | 'expired';
    expires_at: string;
  }) {
    const response = await api.put(`/modules/${moduleId}/features/${featureId}`, featureData);
    return response.data;
  },

  async deleteFeature(moduleId: string, featureId: string) {
    const response = await api.delete(`/modules/${moduleId}/features/${featureId}`);
    return response.data;
  }
};

export const premiumSettingsAPI = {
  getAll: async () => {
    const res = await api.get('/premium-limits');
    return res.data;
  },

  getAllCategories: async () => {
    const res = await api.get('/premium-limits/categories');
    return res.data?.data || [];
  },
  create: async (limit: any) => {
    const res = await api.post('/premium-limits', limit);
    return res.data;
  },
  createCategories: async (limit: any) => {
    const res = await api.post('/premium-limits/categories', limit);
    return res.data;
  },
  update: async (id: string, data: any) => {
    const res = await api.put(`/premium-limits/${id}`, data);
    return res.data;
  },
  delete: async (id: string) => {
    return api.delete(`/premium-limits/${id}`);
  },
  getSubHistory: async (region?: string) => {
    const res = await api.get('/subscription-history', { params: { region } });
    return res.data
  }
};

export const emailLogsAPI = {
  getAll: async (params: any) => {
    const res = await api.get('/email-logs/user-tracking', { params });
    return res.data;
  }
};

export const couponCodesAPI = {
  getAll: async () => {
    const res = await api.get('/coupon-codes');
    return res.data;
  },
  createCouponCode: async (code: any) => {
    const res = await api.post('/coupon-codes', code);
    return res.data;
  },
  deleteCouponCode: async (id: string) => {
    const res = await api.delete(`/coupon-codes/${id}`);
    return res.data;
  },

};

export const downtimeAPI = {
  getStatus: async () => {
    const res = await api.get('/downtime-status');
    return res.data;
  },
  updateStatus: async (status: boolean) => {
    const res = await api.post('/downtime-status', { status });
    return res.data;
  }
};

export const oneonOneMockinterviewAPI = {
  getAll: async () => {
    const res = await api.get('/oneonone-mockinterview');
    return res.data;
  },
  getBookings: async () => {
    const res = await api.get('/oneonone-mockinterview/get-bookings');
    return res.data;
  },
  create: async (data: any) => {
    const res = await api.post('/oneonone-mockinterview', data);
    return res.data;
  },
  update: async (id: string, data: any) => {
    // Update interviewer (not booking)
    const res = await api.put(`/oneonone-mockinterview/${id}`, data);
    return res.data;
  },

  confirmCompletion: async (id: string, data: any) => {
    const res = await api.put(`/oneonone-mockinterview/confirm-completion/${id}`, data);
    return res.data;
  },

  updateBooking: async (id: string, data: {
    interviewer_name?: string;
    interviewer_email?: string;
    candidate_name?: string;
    candidate_email?: string;
    scheduled_date?: string;
    scheduled_time?: string;
  }) => {
    // API endpoint: PUT /schedule-interview/:id
    // Backend automatically:
    // - Updates booking with provided fields (uses existing values if not provided)
    // - Generates new Google Meet link
    // - Sends email to candidate with updated details
    const res = await api.put(`/oneonone-mockinterview/schedule-interview/${id}`, data);
    return res.data;
  },
  delete: async (id: string) => {
    const res = await api.delete(`/oneonone-mockinterview/${id}`);
    return res.data;
  },
  createBooking: async (data: {
    interviewer_name: string;
    interviewer_email: string;
    candidate_name: string;
    candidate_email: string;
    interview_date?: string;
    interview_time?: string;
  }) => {
    const res = await api.post('/oneonone-mockinterview/schedule-interview', data);
    return res.data;
  }
};

axios.defaults.withCredentials = false;
export const authRoutesAPI = {
  createSignup: async (signupDetails: any) => {
    const res = await api.post('/authRoutes/signup', signupDetails);
    return res.data
  },
  createLoginUser: async (loginDetails: any) => {
    const res = await api.post('/authRoutes/login', loginDetails);
    return res.data
  },
  createLogoutUser: async () => {
    const res = await api.post('/authRoutes/logout');
    return res.data
  },
  getAll: async () => {
    const res = await api.get('/authRoutes/auth/check', { headers: { Authorization: `Bearer ${sessionStorage.getItem('jwtToken')}` }, withCredentials: false });
    return res.data;
  },
  verifyOTP: async (otp: any) => {
    const res = await api.post('/authRoutes/verify-otp', otp);
    return res.data
  },
}

export const internshipSessionAPI = {
  getAll: async () => {
    const res = await api.get('/internship-sessions');
    return res.data;
  },
  getInternshipApplications: async () => {
    const res = await api.get('/internship-sessions/get-internship-applications');
    return res.data;
  },
  updateApplicationEndorsement: async (userId: string, endorsement: any) => {
    const res = await api.put(`/internship-sessions/applications/${userId}/endorsement`, { endorsement });
    return res.data;
  },
  create: async (data: {
    title: string;
    date: string;
    time: string;
    dateTime?: string;
    join_url?: string;
    session_type?: string;
    domain?: string;
  }) => {
    const res = await api.post('/internship-sessions', data);
    return res.data;
  },
  update: async (id: string, data: {
    title?: string;
    date?: string;
    time?: string;
    dateTime?: string;
    join_url?: string;
    session_type?: string;
    domain?: string;
  }) => {
    const res = await api.put(`/internship-sessions/${id}`, data);
    return res.data;
  },
  delete: async (id: string) => {
    const res = await api.delete(`/internship-sessions/${id}`);
    return res.data;
  }
};

export const mentorsAPI = {
  getAll: async () => {
    const res = await api.get('/mentors');
    return res.data;
  },
  create: async (data: any) => {
    const res = await api.post('/mentors', data);
    return res.data;
  },
  update: async (id: string, data: any) => {
    const res = await api.put(`/mentors/${id}`, data);
    return res.data;
  },
  delete: async (id: string) => {
    const res = await api.delete(`/mentors/${id}`);
    return res.data;
  }
};

export const mentorChatsAPI = {
  getAllChats: async (mentorId?: string) => {
    const res = await api.get('/mentors/chats/all', { params: { mentor_id: mentorId } });
    return res.data;
  },
  getChatHistory: async (mentorId: string, userId: string) => {
    const res = await api.get(`/mentors/chats/${mentorId}/${userId}`);
    return res.data;
  },
  sendMessage: async (mentorId: string, userId: string, message: string, from: 'user' | 'mentor') => {
    const res = await api.post('/mentors/chats/message', {
      mentor_id: mentorId,
      user_id: userId,
      message,
      message_from: from
    });
    return res.data;
  }
};

const apis = {
  feedbackAPI,
  userAPI,
  featuresAPI,
  rejectedJobsAPI,
  companiesAPI,
  emailTemplatesAPI,
  infraCostAPI,
  featureModulesAPI,
  premiumSettingsAPI,
  authRoutesAPI,
  downtimeAPI,
  oneonOneMockinterviewAPI,
  couponCodesAPI,
  internshipSessionAPI,
  mentorsAPI,
  mentorChatsAPI,
};

export default apis;
